import { browserAccountStateSessionRepository, isTransientSessionStatus } from "./account-state-session-repository.js";
import { createKratosBrowserClient, KratosBrowserClientError } from "./kratos-browser-client.js";
import { identityBrowserContext } from "./kratos-browser-contract.js";

const CONTRACT_VERSION = 1;
// An in-page request budget, not an identity-expiry policy. Kratos owns that.
const RENEWAL_ATTEMPT_INTERVAL_MS = 60 * 60 * 1000;
const SESSION_RECOVERY_INTERVAL_MS = 60 * 1000;
const REPOSITORY_BRAND = Symbol.for(
  "fishystuff.kratos-identity-session-repository.v1",
);
const BROWSER_REPOSITORY_BRAND = Symbol.for(
  "fishystuff.browser-kratos-identity-session-repository.v1",
);
const BROWSER_REPOSITORY_SLOT = Symbol.for(
  "fishystuff.browser-kratos-identity-session-repository.slot.v1",
);

function freezeState(state) {
  return Object.freeze({ ...state });
}

export function createKratosIdentitySessionRepository({
  accountSessionRepository,
  client,
  eventTarget = null,
  now = Date.now,
} = {}) {
  if (
    !accountSessionRepository
    || !["load", "refresh", "state", "subscribe"].every(
      (method) => typeof accountSessionRepository[method] === "function",
    )
    || !client
    || typeof client.session !== "function"
    || typeof client.renewSession !== "function"
    || typeof now !== "function"
  ) {
    throw new TypeError(
      "Identity session repository requires account-session and Ory clients.",
    );
  }

  let currentState = freezeState({ kind: "uninitialized" });
  let requestRevision = 0;
  let requestController = null;
  let loadPromise = null;
  let disposed = false;
  let lastRenewalAttempt = -Infinity;
  let renewalAccountId = null;
  let lastSessionReadAttempt = -Infinity;
  let activityRecoveryAllowed = false;
  const listeners = new Set();

  function state() {
    return currentState;
  }

  function deliver(listener) {
    try {
      listener(currentState);
    } catch (_error) {
      globalThis.reportError?.(new Error("Identity session subscriber failed."));
    }
  }

  function publish(nextState) {
    if (disposed) return currentState;
    const previous = currentState;
    currentState = freezeState(nextState);
    const unchanged = previous.kind === currentState.kind
      && previous.accountId === currentState.accountId
      && previous.session === currentState.session;
    if (!unchanged) {
      for (const listener of listeners) deliver(listener);
    }
    return currentState;
  }

  async function performLoad({ refreshAccount = false } = {}) {
    if (disposed) return currentState;
    lastSessionReadAttempt = now();
    activityRecoveryAllowed = false;
    requestRevision += 1;
    const revision = requestRevision;
    requestController?.abort();
    const controller = new AbortController();
    requestController = controller;
    if (currentState.kind === "uninitialized") publish({ kind: "loading" });

    try {
      const accountState = await accountSessionRepository[
        refreshAccount ? "refresh" : "load"
      ]();
      if (disposed || revision !== requestRevision) return currentState;
      if (accountState.kind === "signedOut") {
        return publish({ kind: "signedOut" });
      }
      if (accountState.kind !== "ready") {
        activityRecoveryAllowed = accountState.kind === "unavailable" && accountState.retryable === true;
        return publish({ kind: "unavailable", reason: "account-session-unavailable" });
      }
      if (renewalAccountId !== accountState.accountId || now() - lastRenewalAttempt >= RENEWAL_ATTEMPT_INTERVAL_MS) {
        renewalAccountId = accountState.accountId;
        lastRenewalAttempt = now();
        try {
          await client.renewSession({ signal: controller.signal });
        } catch (error) {
          if (controller.signal.aborted || error?.name === "AbortError") return currentState;
          // Renewal availability cannot erase a still-valid identity. The next
          // authoritative read decides whether the session actually expired.
          globalThis.reportError?.(new Error("Automatic session renewal failed."));
        }
        if (disposed || revision !== requestRevision) return currentState;
      }
      const identity = await client.session({ signal: controller.signal });
      if (disposed || revision !== requestRevision) return currentState;
      if (identity.kind === "signedOut") {
        return publish({ kind: "signedOut" });
      }
      if (identity.kind !== "ready") {
        return publish({
          kind: "unavailable",
          accountId: accountState.accountId,
          reason: "identity-session-unavailable",
        });
      }
      return publish({
        kind: "ready",
        accountId: accountState.accountId,
        session: identity.session,
      });
    } catch (error) {
      if (
        disposed
        || revision !== requestRevision
        || controller.signal.aborted
        || error?.name === "AbortError"
      ) {
        return currentState;
      }
      const accountState = accountSessionRepository.state();
      activityRecoveryAllowed = error instanceof TypeError
        || (error instanceof KratosBrowserClientError && error.kind === "transport"
          && isTransientSessionStatus(error.status));
      return publish({
        kind: "unavailable",
        ...(accountState.kind === "ready" ? { accountId: accountState.accountId } : {}),
        reason: "identity-session-request-failed",
      });
    } finally {
      if (revision === requestRevision) requestController = null;
    }
  }

  function startLoad(options) {
    if (disposed) return Promise.resolve(currentState);
    if (loadPromise) return loadPromise;
    const pending = performLoad(options);
    loadPromise = pending;
    void pending.finally(() => {
      if (loadPromise === pending) loadPromise = null;
    });
    return pending;
  }

  function load() {
    if (!["uninitialized", "loading"].includes(currentState.kind)) {
      return Promise.resolve(currentState);
    }
    return startLoad({ refreshAccount: false });
  }

  async function refresh() {
    if (loadPromise) await loadPromise;
    return startLoad({ refreshAccount: true });
  }

  function subscribe(listener) {
    if (typeof listener !== "function") {
      throw new TypeError("Identity session listener must be a function.");
    }
    if (disposed) return () => {};
    listeners.add(listener);
    deliver(listener);
    return () => listeners.delete(listener);
  }

  const unsubscribeAccount = accountSessionRepository.subscribe((accountState) => {
    if (disposed) return;
    if (accountState.kind === "signedOut") {
      renewalAccountId = null;
      requestRevision += 1;
      requestController?.abort();
      requestController = null;
      publish({ kind: "signedOut" });
      return;
    }
    if (
      accountState.kind === "ready"
      && (
        currentState.kind === "signedOut"
        || (currentState.kind === "ready" && currentState.accountId !== accountState.accountId)
      )
    ) {
      void refresh();
    }
  });

  function handleActivity() {
    if (disposed || loadPromise) return;
    const renewalDue = currentState.kind === "ready"
      && now() - lastRenewalAttempt >= RENEWAL_ATTEMPT_INTERVAL_MS;
    const recoveryDue = currentState.kind === "unavailable" && activityRecoveryAllowed
      && now() - lastSessionReadAttempt >= SESSION_RECOVERY_INTERVAL_MS;
    if (renewalDue || recoveryDue) void refresh();
  }

  // A revisited open tab also renews. No background heartbeat keeps abandoned
  // sessions alive; listeners belong to this repository and are torn down here.
  const activityEvents = ["focus", "pageshow", "pointerdown", "keydown"];
  for (const event of activityEvents) eventTarget?.addEventListener(event, handleActivity, { passive: true });

  function dispose() {
    if (disposed) return;
    disposed = true;
    requestRevision += 1;
    requestController?.abort();
    requestController = null;
    loadPromise = null;
    listeners.clear();
    unsubscribeAccount();
    for (const event of activityEvents) eventTarget?.removeEventListener(event, handleActivity);
  }

  return Object.freeze({
    [REPOSITORY_BRAND]: CONTRACT_VERSION,
    dispose,
    load,
    refresh,
    state,
    subscribe,
  });
}

function existingBrowserRepository(windowRef) {
  const repository = windowRef[BROWSER_REPOSITORY_SLOT];
  if (repository === undefined) return null;
  if (
    repository?.[BROWSER_REPOSITORY_BRAND] !== CONTRACT_VERSION
    || !["load", "refresh", "state", "subscribe"].every(
      (method) => typeof repository[method] === "function",
    )
  ) {
    throw new TypeError("The shared browser identity session repository is incompatible.");
  }
  return repository;
}

export function browserKratosIdentitySessionRepository() {
  const windowRef = globalThis.window;
  if (!windowRef || typeof windowRef !== "object") {
    throw new TypeError("The browser identity session repository requires a window.");
  }
  const existing = existingBrowserRepository(windowRef);
  if (existing) return existing;

  const context = identityBrowserContext({
    apiBaseUrl: windowRef.__fishystuffApiBaseUrl,
    identityBaseUrl: windowRef.__fishystuffRuntimeConfig?.identity?.baseUrl,
    locationLike: windowRef.location,
    passkeyRpId: windowRef.__fishystuffRuntimeConfig?.identity?.passkeyRpId,
    returnPath: "/account/",
  });
  const owner = createKratosIdentitySessionRepository({
    accountSessionRepository: browserAccountStateSessionRepository(),
    client: createKratosBrowserClient({ context }),
    eventTarget: windowRef,
  });
  const repository = Object.freeze({
    [BROWSER_REPOSITORY_BRAND]: CONTRACT_VERSION,
    load: owner.load,
    refresh: owner.refresh,
    state: owner.state,
    subscribe: owner.subscribe,
  });
  Object.defineProperty(windowRef, BROWSER_REPOSITORY_SLOT, {
    configurable: false,
    enumerable: false,
    value: repository,
    writable: false,
  });
  void owner.load();
  return repository;
}

export function refreshBrowserKratosIdentitySessionRepository() {
  const hadRepository = Boolean(
    globalThis.window && existingBrowserRepository(globalThis.window),
  );
  const repository = browserKratosIdentitySessionRepository();
  return hadRepository ? repository.refresh() : repository.load();
}
